<template>
  <!-- Main scroll container -->
  <div class="setup-view" ref="setupViewRef" @scroll="onScroll">
    
    <!-- Floating Side Navigation -->
    <nav class="sidenav" id="sidenav">
      <a 
        v-for="section in setupSections" 
        :key="section.id"
        :href="'#' + section.id"
        :class="{ active: activeSection === section.id }"
        @click.prevent="scrollTo(section.id)"
      >
        <span class="pct">{{ getSectionCompleted(section) }}/{{ section.tasks.length }}</span>
        <span>{{ section.shortTitle }}</span>
        <span class="dot" :style="{ background: section.color }"></span>
      </a>
    </nav>

    <!-- Main Playbook Content -->
    <main class="setup-main">
      <div class="eyebrow"><span class="n">{{ completionPercentage }}%</span> &nbsp;·&nbsp; new mac setup</div>
      <h1>Setup <span class="grad">kit.</span></h1>
      <p class="lede">Everything I run on a fresh Mac to get back to my normal setup.</p>
      <p class="lede-sub">Check things off as you go — it's saved locally in this browser 🛠️</p>

      <!-- Dynamically rendered sections based on setupSections array -->
      <section 
        v-for="section in setupSections" 
        :key="section.id" 
        :id="section.id" 
        class="item"
      >
        <div class="item-head">
          <span class="dot" :style="{ background: section.color }"></span>
          <h2>{{ section.title }}</h2>
        </div>
        
        <p v-if="section.desc" class="desc" v-html="section.desc"></p>

        <!-- Tasks list with attached commands and notes -->
        <div class="checklist">
          <div 
            v-for="task in section.tasks" 
            :key="task.id" 
            class="task-block"
          >
            <!-- Checkbox Row -->
            <label 
              class="check-row" 
              :class="{ done: completedTasks[task.id] }"
            >
              <input v-model="completedTasks[task.id]" type="checkbox" />
              <div class="box">
                <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                  <path d="M1 4.5L4 7.5L10 1" stroke="#0a0a0a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="row-body">
                <div class="row-title">{{ task.label }}</div>
              </div>
            </label>

            <!-- Embedded Task Note -->
            <div v-if="task.note" class="task-note" v-html="task.note"></div>

            <!-- Dedicated Download Card if task calls for it -->
            <div v-if="task.isDownload" class="download-card">
              <div class="dl-info">
                <span class="dl-filename">monokai-iterm.json</span>
                <span class="dl-meta">iTerm2 Color Scheme · JSON</span>
              </div>
              <button class="dl-btn" @click="downloadItermProfile">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download
              </button>
            </div>

            <!-- Embedded Command Code Block -->
            <div v-if="task.code" class="term">
              <div class="term-bar">
                <button class="copy-btn" @click="copyText($event, task.code.raw)">copy</button>
              </div>
              <pre v-html="task.code.html"></pre>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { computed, reactive, watch, ref, onMounted } from 'vue'
import confetti from 'canvas-confetti'

const STORAGE_KEY = 'vunk-pro-work-mac-setup'

const setupSections = [
  {
    id: 'permissions',
    color: 'var(--c-skill)',
    shortTitle: 'permissions',
    title: '1. Permissions & Recon',
    desc: 'Verify administrative capabilities first before running system-level scripts.',
    tasks: [
      { 
        id: 'permissions-1', 
        label: 'Check for sudo access',
        note: 'If <code>sudo -v</code> prompts for a password and succeeds, you have local admin privileges.',
        code: {
          raw: 'sudo -v',
          html: 'sudo -v'
        }
      },
      { id: 'permissions-2', label: 'Ask IT: local admin or MDM-locked?' },
      { id: 'permissions-3', label: 'Ask IT: shared App Store account or corporate SSO?' }
    ]
  },
  {
    id: 'tweaks',
    color: 'var(--c-home)',
    shortTitle: 'tweaks',
    title: '2. System Tweaks',
    desc: 'Configure OS behaviors, keyboard speeds, and Finder settings.',
    tasks: [
      { 
        id: 'tweaks-tt-finder', 
        label: 'Finder: TinkerTool-equivalent tweaks',
        note: 'Mirrors what TinkerTool\'s Finder pane currently has checked. "Show hidden and system files" is already covered by the task below (same <code>AppleShowAllFiles</code> key). Two checked items don\'t have a public <code>defaults</code> equivalent — "Animate selecting Info panel categories" and "Collect all data first before displaying folder contents" (a newer, network-folder-only TinkerTool option) — so TinkerTool is still the only way to set those two specifically.',
        code: {
          raw: '# Add "Quit Finder" (Cmd+Q) to the Finder menu\ndefaults write com.apple.finder QuitMenuItem -bool true\n\n# Show the full path in the Finder window title bar\ndefaults write com.apple.finder _FXShowPosixPathInTitle -bool true\n\n# Animate opening Info panels and Desktop icons\ndefaults write com.apple.finder AnimateInfoPanes -bool true\n\nkillall Finder',
          html: '<span class="s"># Add "Quit Finder" (Cmd+Q) to the Finder menu</span>\ndefaults write com.apple.finder QuitMenuItem -bool true\n\n<span class="s"># Show the full path in the Finder window title bar</span>\ndefaults write com.apple.finder _FXShowPosixPathInTitle -bool true\n\n<span class="s"># Animate opening Info panels and Desktop icons</span>\ndefaults write com.apple.finder AnimateInfoPanes -bool true\n\nkillall Finder'
        }
      },
      { 
        id: 'tweaks-tt-dock', 
        label: 'Dock: TinkerTool-equivalent tweaks',
        note: 'Genie is already macOS\'s default minimize effect, included here for completeness. TinkerTool\'s Dock "Spacing" slider isn\'t a single public setting — it works by inserting spacer tiles into the Dock, not a global default — so there\'s no one-line terminal equivalent for that one.',
        code: {
          raw: '# Dim the icons of hidden applications in the Dock\ndefaults write com.apple.dock showhidden -bool true\n\n# Remove the delay before the Dock appears when hovering at the screen edge\ndefaults write com.apple.dock autohide-delay -float 0\n\n# Minimize windows using the genie effect\ndefaults write com.apple.dock mineffect -string genie\n\nkillall Dock',
          html: '<span class="s"># Dim the icons of hidden applications in the Dock</span>\ndefaults write com.apple.dock showhidden -bool true\n\n<span class="s"># Remove the delay before the Dock appears when hovering at the screen edge</span>\ndefaults write com.apple.dock autohide-delay -float 0\n\n<span class="s"># Minimize windows using the genie effect</span>\ndefaults write com.apple.dock mineffect -string genie\n\nkillall Dock'
        }
      },
      { 
        id: 'tweaks-tt-general', 
        label: 'General: power button & scrolling behavior',
        note: 'The power button setting lives in the login window domain, so it can need a logout/login to fully apply.',
        code: {
          raw: '# Power button: press and release puts the Mac to sleep\ndefaults write com.apple.loginwindow PowerButtonSleepsSystem -bool true\n\n# Clicking in a scrollbar track jumps to the next page, not to the clicked spot\ndefaults write -g AppleScrollerPagingBehavior -bool false\n\n# Keep smooth (animated) scrolling on\ndefaults write -g NSScrollAnimationEnabled -bool true',
          html: '<span class="s"># Power button: press and release puts the Mac to sleep</span>\ndefaults write com.apple.loginwindow PowerButtonSleepsSystem -bool true\n\n<span class="s"># Clicking in a scrollbar track jumps to the next page, not to the clicked spot</span>\ndefaults write -g AppleScrollerPagingBehavior -bool false\n\n<span class="s"># Keep smooth (animated) scrolling on</span>\ndefaults write -g NSScrollAnimationEnabled -bool true'
        }
      },
      { 
        id: 'tweaks-tt-screenshot', 
        label: 'Screenshots: format, shadow & thumbnail',
        note: 'This is macOS\'s built-in Screenshot utility pane in TinkerTool (not a separate app).',
        code: {
          raw: '# Save screenshots as PNG\ndefaults write com.apple.screencapture type png\n\n# Include a drop shadow when capturing a window\ndefaults write com.apple.screencapture disable-shadow -bool false\n\n# Keep the date & time in the screenshot filename\ndefaults write com.apple.screencapture include-date -bool true\n\n# Show the floating thumbnail after taking a screenshot\ndefaults write com.apple.screencapture show-thumbnail -bool true\n\nkillall SystemUIServer',
          html: '<span class="s"># Save screenshots as PNG</span>\ndefaults write com.apple.screencapture type png\n\n<span class="s"># Include a drop shadow when capturing a window</span>\ndefaults write com.apple.screencapture disable-shadow -bool false\n\n<span class="s"># Keep the date &amp; time in the screenshot filename</span>\ndefaults write com.apple.screencapture include-date -bool true\n\n<span class="s"># Show the floating thumbnail after taking a screenshot</span>\ndefaults write com.apple.screencapture show-thumbnail -bool true\n\nkillall SystemUIServer'
        }
      },
      { 
        id: 'tweaks-tt-crash', 
        label: 'Desktop: show crash reports',
        note: 'Use <code>developer</code> instead of <code>basic</code> for the more detailed dialog, or <code>none</code> to suppress it entirely.',
        code: {
          raw: '# Show the standard crash dialog when an app quits unexpectedly\ndefaults write com.apple.CrashReporter DialogType basic',
          html: '<span class="s"># Show the standard crash dialog when an app quits unexpectedly</span>\ndefaults write com.apple.CrashReporter DialogType basic'
        }
      },
      { 
        id: 'tweaks-2', 
        label: 'Kill slow system animations',
        code: {
          raw: 'defaults write -g NSAutomaticWindowAnimationsEnabled -bool false\ndefaults write -g NSWindowResizeTime -float 0.001\ndefaults write com.apple.dock autohide-time-modifier -float 0\ndefaults write com.apple.dock expose-animation-duration -float 0\nkillall Dock',
          html: 'defaults write -g NSAutomaticWindowAnimationsEnabled -bool false\ndefaults write -g NSWindowResizeTime -float 0.001\ndefaults write com.apple.dock autohide-time-modifier -float 0\ndefaults write com.apple.dock expose-animation-duration -float 0\nkillall Dock'
        }
      },
      { 
        id: 'tweaks-3', 
        label: 'Finder: Show hidden files, extensions, and path bar',
        code: {
          raw: 'defaults write com.apple.finder AppleShowAllFiles -bool true\ndefaults write com.apple.finder ShowPathbar -bool true\ndefaults write NSGlobalDomain AppleShowAllExtensions -bool true\nkillall Finder',
          html: 'defaults write com.apple.finder AppleShowAllFiles -bool true\ndefaults write com.apple.finder ShowPathbar -bool true\ndefaults write NSGlobalDomain AppleShowAllExtensions -bool true\nkillall Finder'
        }
      },
      { 
        id: 'tweaks-4', 
        label: 'Keyboard: Max key repeat speed & disable press-and-hold',
        note: 'Requires a system restart to apply to all applications.',
        code: {
          raw: 'defaults write NSGlobalDomain KeyRepeat -int 1\ndefaults write NSGlobalDomain InitialKeyRepeat -int 10\ndefaults write NSGlobalDomain ApplePressAndHoldEnabled -bool false',
          html: 'defaults write NSGlobalDomain KeyRepeat -int 1\ndefaults write NSGlobalDomain InitialKeyRepeat -int 10\ndefaults write NSGlobalDomain ApplePressAndHoldEnabled -bool false'
        }
      },
      { 
        id: 'tweaks-5', 
        label: 'Add custom no-sleep bash functions',
        note: '<strong style="color: var(--c-home);">!NB! FINTECH POLICY WARNING:</strong> Modifying sleep behaviors to bypass auto-lock is almost certainly against internal MDM policies and security compliance. Use at your own risk.',
        code: {
          raw: '# No-sleep functionality\nnosleep-on() {\n  sudo pmset -a disablesleep 1 displaysleep 0\n  caffeinate -dis &\n  echo $! > /tmp/nosleep_pid\n  echo "nosleep ON (caffeinate pid $!)"\n}\n\nnosleep-off() {\n  sudo pmset -a disablesleep 0 displaysleep 10\n  if [ -f /tmp/nosleep_pid ]; then\n    kill "$(cat /tmp/nosleep_pid)" 2>/dev/null\n    rm /tmp/nosleep_pid\n  fi\n  echo "nosleep OFF"\n}',
          html: '<span class="s"># No-sleep functionality</span>\nnosleep-on() {\n  sudo pmset -a disablesleep 1 displaysleep 0\n  caffeinate -dis &amp;\n  echo $! &gt; /tmp/nosleep_pid\n  echo "nosleep ON (caffeinate pid $!)"\n}\n\nnosleep-off() {\n  sudo pmset -a disablesleep 0 displaysleep 10\n  if [ -f /tmp/nosleep_pid ]; then\n    kill "$(cat /tmp/nosleep_pid)" 2&gt;/dev/null\n    rm /tmp/nosleep_pid\n  fi\n  echo "nosleep OFF"\n}'
        }
      }
    ]
  },
  {
    id: 'apps',
    color: 'var(--c-about)',
    shortTitle: 'apps',
    title: '3. Homebrew & Apps',
    desc: 'Install key application binaries, GUI utilities, and command-line tools.',
    tasks: [
      { 
        id: 'apps-1', 
        label: 'Install Homebrew',
        code: {
          raw: '/bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"',
          html: '/bin/bash -c <span class="s">"$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"</span>'
        }
      },
      { 
        id: 'apps-2', 
        label: 'Install Core Productivity Apps & CLI utilities',
        note: 'Includes Raycast, iTerm2, Spotify, Slack, VS Code, Git, OrbStack (Docker alternative), DBeaver, fzf, bat, jq, and gh CLI.',
        code: {
          raw: 'brew install git fzf bat jq yq gh\nbrew install --cask iterm2 raycast spotify slack visual-studio-code jetbrains-toolbox orbstack dbeaver-community',
          html: 'brew install git fzf bat jq yq gh\nbrew install --cask iterm2 raycast spotify slack visual-studio-code jetbrains-toolbox orbstack dbeaver-community'
        }
      },
      { 
        id: 'apps-3', 
        label: 'Open JetBrains Toolbox & Install IntelliJ IDEA Ultimate' 
      },
      { 
        id: 'apps-4', 
        label: 'Enable AdGuard Extensions inside Safari Preferences' 
      },
      { 
        id: 'apps-5', 
        label: 'Install Hidden Bar',
        note: 'Menu bar icon overflow management — pairs well with Raycast/AdGuard/Vorssaint all sitting in the same menu bar.',
        code: {
          raw: 'brew install --cask hiddenbar',
          html: 'brew install --cask hiddenbar'
        }
      },
      { 
        id: 'apps-6', 
        label: 'Install Rectangle (Window Management)',
        note: 'Standard window snapping. (Vorssaint and Caffeine equivalents omitted to prevent triggering security/MDM agent alerts).',
        code: {
          raw: 'brew install --cask rectangle',
          html: 'brew install --cask rectangle'
        }
      },
      { 
        id: 'apps-7', 
        label: 'Vorssaint: install Window Layout & Keep Awake & Displays features',
        note: 'Features hub → install <b>Window Layout</b> (Rectangle replacement), <b>Keep Awake</b> (Caffeine + nosleep functions replacement), and <b>Displays</b> (Vivid replacement). Grant Accessibility when prompted.'
      },
    ]
  },
  {
    id: 'terminal',
    color: 'var(--c-proj)',
    shortTitle: 'terminal',
    title: '4. Terminal & Shell',
    desc: 'Set up terminal emulators, custom shell integrations, and styling.',
    tasks: [
      { 
        id: 'terminal-1', 
        label: 'Install JetBrains Mono font',
        code: {
          raw: 'brew install --cask font-jetbrains-mono',
          html: 'brew install --cask font-jetbrains-mono'
        }
      },
      { 
        id: 'terminal-2', 
        label: 'Install Oh My Zsh + Productivity Plugins',
        code: {
          raw: 'sh -c "$(curl -fsSL https://raw.githubusercontent.com/ohmyzsh/ohmyzsh/master/tools/install.sh)"\n\ngit clone https://github.com/zsh-users/zsh-autosuggestions ${ZSH_CUSTOM:-~/.oh-my-zsh/custom}/plugins/zsh-autosuggestions\ngit clone https://github.com/zsh-users/zsh-syntax-highlighting.git ${ZSH_CUSTOM:-~/.oh-my-zsh/custom}/plugins/zsh-syntax-highlighting',
          html: 'sh -c <span class="s">"$(curl -fsSL https://raw.githubusercontent.com/ohmyzsh/ohmyzsh/master/tools/install.sh)"</span>\n\ngit clone https://github.com/zsh-users/zsh-autosuggestions ${ZSH_CUSTOM:-~/.oh-my-zsh/custom}/plugins/zsh-autosuggestions\ngit clone https://github.com/zsh-users/zsh-syntax-highlighting.git ${ZSH_CUSTOM:-~/.oh-my-zsh/custom}/plugins/zsh-syntax-highlighting'
        }
      },
      { 
        id: 'terminal-3', 
        label: 'Download & import iTerm2 profile',
        note: 'Import via iTerm2: <code>Preferences → Profiles → Other Actions → Import JSON Profiles</code>.',
        isDownload: true,
        code: {
          raw: 'curl -O https://gist.githubusercontent.com/1vunk/7b3487832eee7a6de0da852348e24816/raw/862867c213b6b316e514f2850b849d530edbc858/monokai-iterm.json',
          html: 'curl -O <span class="s">https://gist.githubusercontent.com/1vunk/7b3487832eee7a6de0da852348e24816/raw/862867c213b6b316e514f2850b849d530edbc858/monokai-iterm.json</span>'
        }
      }
    ]
  },
  {
    id: 'devenv',
    color: 'var(--c-exp)',
    shortTitle: 'dev env',
    title: '5. Dev Environment',
    desc: 'Language runtimes, cloud CLI tools, and Git identity.',
    tasks: [
      { 
        id: 'devenv-1', 
        label: 'Install SDKMAN! & Java',
        note: 'Manages JVM versions cleanly. <strong style="color: var(--c-skill);">Verify Version:</strong> Java 25 is the latest LTS, but check internal onboarding docs to see if the team is still standardizing on Java 21 or 17.',
        code: {
          raw: 'curl -s "https://get.sdkman.io" | bash\nsource "$HOME/.sdkman/bin/sdkman-init.sh"\nsdk install java 25.0.2-tem\n# sdk install java 21.0.2-tem\nsdk install springboot',
          html: 'curl -s <span class="s">"https://get.sdkman.io"</span> | bash\nsource "$HOME/.sdkman/bin/sdkman-init.sh"\nsdk install java 25.0.2-tem\n<span class="s"># sdk install java 21.0.2-tem</span>\nsdk install springboot'
        }
      },
      { 
        id: 'devenv-2', 
        label: 'Install Python & nvm',
        code: {
          raw: 'brew install python\ncurl -o- https://raw.githubusercontent.com/nvm-sh/nvm/v0.40.7/install.sh | bash',
          html: 'brew install python\ncurl -o- <span class="s">https://raw.githubusercontent.com/nvm-sh/nvm/v0.40.7/install.sh</span> | bash'
        }
      },
      { 
        id: 'devenv-3', 
        label: 'Install Kafka & kcat (CLI consumer)',
        code: {
          raw: 'brew install kafka kcat',
          html: 'brew install kafka kcat'
        }
      },
      { 
        id: 'devenv-4', 
        label: 'Configure Git identity & generate SSH key',
        code: {
          raw: 'git config --global user.name "Sandor Vunk"\ngit config --global user.email "your@email.com"\n\nssh-keygen -t ed25519 -C "your@email.com"\ncat ~/.ssh/id_ed25519.pub | pbcopy',
          html: 'git config --global user.name "Sandor Vunk"\ngit config --global user.email "<span class="s">your@email.com</span>"\n\nssh-keygen -t ed25519 -C "<span class="s">your@email.com</span>"\ncat ~/.ssh/id_ed25519.pub | pbcopy'
        }
      },
      { 
        id: 'devenv-5', 
        label: 'VS Code: Java/Spring extension pack + settings',
        note: 'Installs the Java pack, Spring Boot dashboard, Docker, GitLens, and REST client. Then applies house-style settings.json.',
        code: {
          raw: `code --install-extension vscjava.vscode-java-pack
      code --install-extension vscjava.vscode-spring-boot-dashboard
      code --install-extension vmware.vscode-boot-dev-pack
      code --install-extension ms-azuretools.vscode-docker
      code --install-extension eamodio.gitlens
      code --install-extension redhat.vscode-yaml
      code --install-extension humao.rest-client
      code --install-extension mtxr.sqltools
      code --install-extension EditorConfig.EditorConfig`,
          html: `code --install-extension vscjava.vscode-java-pack
      code --install-extension vscjava.vscode-spring-boot-dashboard
      code --install-extension vmware.vscode-boot-dev-pack
      code --install-extension ms-azuretools.vscode-docker
      code --install-extension eamodio.gitlens
      code --install-extension redhat.vscode-yaml
      code --install-extension humao.rest-client
      code --install-extension mtxr.sqltools
      code --install-extension EditorConfig.EditorConfig`
        }
      },
      { 
        id: 'devenv-6', 
        label: 'VS Code: apply settings.json',
        note: 'Paste into <code>~/Library/Application Support/Code/User/settings.json</code>.',
        code: {
          raw: `{
        "editor.formatOnSave": true,
        "editor.rulers": [120],
        "editor.tabSize": 4,
        "files.trimTrailingWhitespace": true,
        "files.insertFinalNewline": true,
        "java.compile.nullAnalysis.mode": "automatic",
        "java.configuration.updateBuildConfiguration": "automatic",
        "git.autofetch": true,
        "terminal.integrated.fontFamily": "JetBrains Mono"
      }`,
          html: `{
        "editor.formatOnSave": true,
        "editor.rulers": [120],
        "editor.tabSize": 4,
        "files.trimTrailingWhitespace": true,
        "files.insertFinalNewline": true,
        "java.compile.nullAnalysis.mode": "automatic",
        "java.configuration.updateBuildConfiguration": "automatic",
        "git.autofetch": true,
        "terminal.integrated.fontFamily": "JetBrains Mono"
      }`
        }
      },
      { 
        id: 'devenv-7', 
        label: 'IntelliJ: Install required plugins',
        note: 'Marketplace: <b>OneMonokai</b>, <b>Atom One Theme</b>, <b>Atom Material Icons</b>, <b>Better Comments</b>, <b>GitHub Copilot</b>.'
      },
      { 
        id: 'devenv-8', 
        label: 'IntelliJ: Apply themes & icons',
        note: 'Appearance Theme: <b>One Monokai</b>.<br>Editor Color Scheme: <b>Atom One Dark</b>.<br>File Icons: Enable <b>Atom Material Icons</b>.'
      },
      { 
        id: 'devenv-9', 
        label: 'IntelliJ: Set typography',
        note: 'Editor → Font → <b>JetBrains Mono</b>, Size <b>14</b>, Line height <b>1.2</b>.'
      },
      { 
        id: 'devenv-10', 
        label: 'IntelliJ: Import Wise code style',
        note: 'Editor → Code Style. Import the shared team scheme or <code>.editorconfig</code> instead of using defaults.'
      },
      {
        id: 'devenv-11',
        label: 'IntelliJ: Turn on Settings Sync (for the future)',
        note: 'Settings → Settings Sync. Log in with your new Wise email now, so this baseline is saved for your next corporate laptop refresh.'
      },
    ]
  }
]

export default {
  name: 'SetupView',
  setup() {
    let storedTasks = {}
    try {
      storedTasks = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    } catch (error) {
      console.warn('Could not read saved setup progress:', error)
    }

    const completedTasks = reactive(storedTasks)
    const allTasks = setupSections.flatMap(section => section.tasks)

    const completedCount = computed(() => allTasks.filter(task => completedTasks[task.id]).length)
    const totalTasks = allTasks.length
    const completionPercentage = computed(() => Math.round((completedCount.value / totalTasks) * 100) || 0)

    watch(completedTasks, value => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
    }, { deep: true })

    const triggerConfetti = () => {
      confetti({
        particleCount: 130,
        spread: 90,
        angle: 60,
        origin: { x: 0, y: 0.7 },
        ticks: 400,
        zIndex: 100,
        colors: ['#ef6461', '#4dd0b0', '#7c8ff0', '#f2c14e', '#e879b0']
      })
      confetti({
        particleCount: 130,
        spread: 90,
        angle: 120,
        origin: { x: 1, y: 0.7 },
        ticks: 400,
        zIndex: 100,
        colors: ['#ef6461', '#4dd0b0', '#7c8ff0', '#f2c14e', '#e879b0']
      })
    }

    watch(completionPercentage, (newVal, oldVal) => {
      if (newVal === 100 && oldVal < 100) {
        triggerConfetti()
      }
    })

    const downloadItermProfile = async () => {
      const gistUrl = 'https://gist.githubusercontent.com/1vunk/7b3487832eee7a6de0da852348e24816/raw/862867c213b6b316e514f2850b849d530edbc858/monokai-iterm.json'
      try {
        const res = await fetch(gistUrl)
        const blob = await res.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'monokai-iterm.json'
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } catch (err) {
        console.error('Failed to download profile:', err)
      }
    }

    const setupViewRef = ref(null)
    const activeSection = ref(setupSections[0].id)

    const getSectionCompleted = (section) => {
      return section.tasks.filter(task => completedTasks[task.id]).length
    }

    const onScroll = () => {
      if (!setupViewRef.value) return
      
      const elements = setupSections.map(sec => document.getElementById(sec.id))
      let current = elements[0]?.id
      
      for (const el of elements) {
        if (el && el.getBoundingClientRect().top < window.innerHeight * 0.4) {
          current = el.id
        }
      }
      activeSection.value = current
    }

    const scrollTo = (id) => {
      const el = document.getElementById(id)
      if (el && setupViewRef.value) {
        setupViewRef.value.scrollTo({
          top: el.offsetTop - 140,
          behavior: 'smooth'
        })
      }
    }

    const copyText = async (event, text) => {
      try {
        await navigator.clipboard.writeText(text)
        const btn = event.target
        const orig = btn.textContent
        btn.textContent = 'copied'
        btn.classList.add('copied')
        setTimeout(() => { 
          btn.textContent = orig; 
          btn.classList.remove('copied') 
        }, 1400)
      } catch (err) {
        console.error('Failed to copy to clipboard', err)
      }
    }

    onMounted(() => {
      onScroll()
    })

    return {
      setupSections,
      completedTasks,
      completionPercentage,
      setupViewRef,
      activeSection,
      getSectionCompleted,
      onScroll,
      scrollTo,
      copyText,
      downloadItermProfile
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

.setup-view {
  --panel: #111111;
  --line: #232323;
  --text: #f2f2f2;
  --text-dim: #8a8a8a;
  --text-dimmer: #5c5c5c;

  --c-home:  #ef6461;
  --c-about: #4dd0b0;
  --c-proj:  #7c8ff0;
  --c-skill: #f2c14e;
  --c-exp:   #e879b0;

  --sans: 'Plus Jakarta Sans', -apple-system, sans-serif;
  --mono: 'JetBrains Mono', 'SF Mono', Menlo, monospace;

  box-sizing: border-box;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  height: calc(var(--vh, 1vh) * 100);
  overflow-y: auto;
  color: var(--text);
  font-family: var(--sans);
  position: relative;
  scroll-behavior: smooth;
}

.setup-main {
  position: relative;
  z-index: 2;
  max-width: 760px;
  margin: 0 auto;
  padding: 190px 40px 160px 40px;
}

.eyebrow { 
  font-family: var(--mono); 
  font-size: 13px; 
  color: var(--text-dim); 
  margin-bottom: 14px; 
  letter-spacing: 0.02em; 
}
.eyebrow .n { color: var(--c-home); }

h1 { 
  font-size: clamp(38px, 6vw, 54px); 
  font-weight: 800; 
  margin: 0 0 18px; 
  letter-spacing: -0.02em; 
}
h1 .grad {
  background: linear-gradient(90deg, #f6a19c 0%, #ef6461 35%, #f2914e 65%, #f2c14e 100%);
  -webkit-background-clip: text; 
  background-clip: text; 
  color: transparent;
}

.lede { font-size: 17px; color: #cfcfcf; max-width: 54ch; margin-bottom: 8px; margin-top: 0; }
.lede-sub { font-size: 15px; color: var(--text-dim); margin-bottom: 56px; margin-top: 0; }

section.item {
  margin-bottom: 40px;
  padding: 22px 24px;
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 14px;
  scroll-margin-top: 140px;
}

.item-head { display: flex; align-items: center; gap: 10px; margin-bottom: 4px; }
.dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
h2 { font-size: 16.5px; font-weight: 700; margin: 0; }

.desc { color: var(--text-dim); font-size: 14px; margin: 6px 0 20px; max-width: 58ch; line-height: 1.55; }

/* Checklist Container & Task Grouping */
.checklist { display: flex; flex-direction: column; gap: 16px; }

.task-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.check-row {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 12px 14px;
  background: #0d0d0d;
  border: 1px solid var(--line);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  width: 100%;
  box-sizing: border-box;
}

.check-row::before,
.check-row::after {
  content: none;
  display: none;
}

.check-row:hover { border-color: #333; }
.check-row.done { background: #0a0a0a; border-color: #1a1a1a; }

.check-row input { display: none; }

.box {
  width: 19px; height: 19px; border-radius: 6px;
  border: 1.5px solid #444; flex-shrink: 0; margin-top: 2px;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.check-row.done .box { background: #fff; border-color: #fff; }
.box svg { opacity: 0; transform: scale(0.6); transition: all 0.15s; }
.check-row.done .box svg { opacity: 1; transform: scale(1); }

.row-body { flex: 1; min-width: 0; }
.row-title { font-size: 14px; font-weight: 600; color: var(--text); transition: color 0.15s; }
.check-row.done .row-title { color: var(--text-dimmer); text-decoration: line-through; text-decoration-color: #3a3a3a; }

.task-note {
  font-size: 12.5px;
  color: var(--text-dim);
  border-left: 2px solid #3a3a3a;
  padding: 4px 12px;
  margin-left: 6px;
}
.task-note :deep(code) { 
  font-family: var(--mono); background: #1a1a1a; padding: 1px 5px; border-radius: 4px; font-size: 12.5px; color: #ddd; 
}

/* Download Card */
.download-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #0d0d0d;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 10px 14px;
  margin-left: 6px;
}

.dl-info { display: flex; flex-direction: column; gap: 2px; }
.dl-filename { font-family: var(--mono); font-size: 12.5px; font-weight: 600; color: var(--text); }
.dl-meta { font-size: 11px; color: var(--text-dimmer); }

.dl-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--mono);
  font-size: 11.5px;
  font-weight: 500;
  color: var(--c-proj);
  background: rgba(124, 143, 240, 0.1);
  border: 1px solid rgba(124, 143, 240, 0.25);
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.dl-btn:hover { background: var(--c-proj); color: #000; border-color: var(--c-proj); }

/* Terminal Blocks */
.term { margin-left: 6px; background: #0a0a0a; border: 1px solid #1c1c1c; border-radius: 8px; overflow: hidden; }
.term-bar { display: flex; align-items: center; justify-content: flex-end; padding: 4px 8px; border-bottom: 1px solid #1c1c1c; }
.copy-btn {
  font-family: var(--mono); font-size: 10.5px; color: var(--text-dim);
  background: transparent; border: 1px solid #2a2a2a; padding: 2px 6px; border-radius: 4px; cursor: pointer;
}
.copy-btn:hover { color: #fff; border-color: #444; }
.copy-btn.copied { color: var(--c-about); border-color: var(--c-about); }

pre { margin: 0; padding: 10px 12px; overflow-x: auto; font-family: var(--mono); font-size: 12px; line-height: 1.55; color: #e6e6e6; }
pre :deep(.s) { color: #f2c14e; }

/* Sidenav */
nav.sidenav {
  position: fixed; 
  top: 50%; 
  right: 40px; 
  transform: translateY(-50%);
  z-index: 10; 
  display: flex; 
  flex-direction: column; 
  gap: 18px; 
  text-align: right;
}
nav.sidenav a {
  display: flex; align-items: center; justify-content: flex-end; gap: 9px;
  font-size: 14px; font-weight: 500; text-decoration: none; color: var(--text-dim); transition: color 0.15s;
}
nav.sidenav a .dot { width: 7px; height: 7px; }
nav.sidenav a:hover, nav.sidenav a.active { color: #fff; }
nav.sidenav a .pct { font-family: var(--mono); font-size: 10.5px; color: var(--text-dimmer); min-width: 28px; text-align: right; }

@media (max-width: 900px) { 
  nav.sidenav { display: none; } 
  .setup-main { padding-top: 120px; padding-left: 20px; padding-right: 20px; }
  .term, .task-note, .download-card { margin-left: 0; }
}
</style>